import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingCart, Menu, X, User, Search, Globe, ChevronDown, LayoutGrid, ArrowRight, MapPin, Heart, Package } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";
import { EMIRATE_NAMES } from "@/data/translations";

export const HEADER_OFFSET = 116;

export const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (!el) return;
  const y = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
  window.scrollTo({ top: y, behavior: "smooth" });
};

export const filterCategory = (id, subcategory = null) => {
  window.dispatchEvent(new CustomEvent("cambroo:filter", { detail: { category: id, subcategory } }));
  scrollTo("#products");
};

export const goToFilter = (navigate, id, subcategory = null) => {
  if (window.location.pathname !== "/") {
    sessionStorage.setItem("cambroo:pending-filter", JSON.stringify({ category: id, subcategory }));
    navigate("/");
  } else {
    filterCategory(id, subcategory);
  }
};

const EMIRATES = Object.keys(EMIRATE_NAMES.en);

export default function Nav() {
  const { count, setCartOpen, categories, wishlist, emirate, setEmirate } = useCart();
  const { lang, setLang, t, isAr } = useLanguage();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [query, setQuery] = useState("");
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === "/search") {
      const q = new URLSearchParams(location.search).get("q");
      if (q) setQuery(q);
    }
  }, [location]);
  const [hoveredCat, setHoveredCat] = useState(null);
  const [locOpen, setLocOpen] = useState(false);

  const hovered = categories.find((c) => c.id === hoveredCat);

  const go = (e, href) => {
    e.preventDefault();
    setMenuOpen(false);
    scrollTo(href);
  };

  const onSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setMenuOpen(false);
    setMegaOpen(false);
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  // Category click -> dedicated category page (noon style)
  const pick = (catId, subId = null) => {
    setMegaOpen(false);
    setHoveredCat(null);
    setMenuOpen(false);
    navigate(`/category/${catId}${subId ? `?sub=${subId}` : ""}`);
  };

  const searchBox = (id) => (
    <form onSubmit={onSearch} className="relative w-full" role="search">
      <Search className="pointer-events-none absolute start-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
      <input
        data-testid={id}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t("search_placeholder")}
        className="w-full rounded-md border border-transparent bg-white py-2.5 pe-4 ps-10 text-sm text-brandink shadow-sm outline-none transition-colors placeholder:text-muted/80 focus:border-link"
      />
    </form>
  );

  return (
    <>
      <header data-testid="main-nav" className="fixed top-0 left-0 right-0 z-50">
        {/* Yellow top bar */}
        <div className="bg-brand">
          <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3 lg:px-8">
            <a
              href="#top"
              onClick={(e) => { e.preventDefault(); navigate("/"); }}
              data-testid="nav-logo"
              className="flex shrink-0 items-baseline gap-2 text-brandink"
            >
              <span className="text-2xl font-extrabold lowercase tracking-tight">cambroo</span>
              <span className="hidden text-[10px] font-medium uppercase tracking-widest text-brandink/70 xl:block">
                {t("brand_sub")}
              </span>
            </a>

            <div className="hidden flex-1 md:block">{searchBox("nav-search-input")}</div>

            <div className="ms-auto flex items-center gap-0.5 md:ms-0">
              {/* Location picker */}
              <div className="relative hidden lg:block">
                <button
                  data-testid="nav-location-button"
                  onClick={() => setLocOpen((o) => !o)}
                  className="flex h-10 items-center gap-1.5 rounded-md px-2 transition-colors hover:bg-branddark"
                >
                  <MapPin className="h-4 w-4 text-brandink" />
                  <span className="text-start leading-tight">
                    <span className="block text-[9px] text-brandink/70">{t("deliver_to")}&nbsp;</span>
                    <span className="block text-[11px] font-bold text-brandink">{EMIRATE_NAMES[lang][emirate] || emirate}</span>
                  </span>
                  <ChevronDown className={`h-3 w-3 text-brandink/70 transition-transform ${locOpen ? "rotate-180" : ""}`} />
                </button>
                {locOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setLocOpen(false)} />
                    <div data-testid="nav-location-menu" className="absolute end-0 top-full z-50 mt-1 w-44 rounded-md border border-line bg-white py-1 shadow-lg">
                      {EMIRATES.map((e) => (
                        <button
                          key={e}
                          data-testid={`nav-location-${e.replace(/\s+/g, "-").toLowerCase()}`}
                          onClick={() => { setEmirate(e); setLocOpen(false); }}
                          className={`block w-full px-3 py-2 text-start text-xs transition-colors hover:bg-page ${
                            emirate === e ? "font-bold text-brandink" : "text-brandink/80"
                          }`}
                        >
                          {EMIRATE_NAMES[lang][e] || e}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <button
                data-testid="language-toggle"
                onClick={() => setLang(lang === "en" ? "ar" : "en")}
                className="flex h-10 items-center gap-1.5 rounded-md px-2.5 text-xs font-bold text-brandink transition-colors hover:bg-branddark"
                aria-label="Switch language"
              >
                <Globe className="h-4 w-4" />
                {lang === "en" ? "العربية" : "EN"}
              </button>
              <Link
                to="/account"
                data-testid="nav-account-button"
                className="flex h-10 flex-col items-center justify-center rounded-md px-2.5 text-brandink transition-colors hover:bg-branddark"
                title={t("nav_account")}
              >
                <User className="h-4 w-4" />
                <span className="hidden text-[9px] font-semibold leading-tight lg:block">{t("nav_account")}</span>
              </Link>
              <Link
                to="/account"
                data-testid="nav-orders-button"
                className="hidden h-10 flex-col items-center justify-center rounded-md px-2.5 text-brandink transition-colors hover:bg-branddark md:flex"
                title={t("nav_orders")}
              >
                <Package className="h-4 w-4" />
                <span className="hidden text-[9px] font-semibold leading-tight lg:block">{t("nav_orders")}</span>
              </Link>
              <Link
                to="/wishlist"
                data-testid="nav-wishlist-button"
                className="relative flex h-10 flex-col items-center justify-center rounded-md px-2.5 text-brandink transition-colors hover:bg-branddark"
                title={t("nav_wishlist")}
              >
                <Heart className="h-4 w-4" />
                <span className="hidden text-[9px] font-semibold leading-tight lg:block">{t("nav_wishlist")}</span>
                {wishlist.length > 0 && (
                  <span
                    data-testid="nav-wishlist-badge"
                    className="absolute end-0 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brandink px-1 text-[9px] font-bold text-white"
                  >
                    {wishlist.length}
                  </span>
                )}
              </Link>
              <button
                data-testid="nav-cart-button"
                onClick={() => setCartOpen(true)}
                className="relative flex h-10 flex-col items-center justify-center rounded-md px-2.5 text-brandink transition-colors hover:bg-branddark"
                aria-label="Open cart"
              >
                <ShoppingCart className="h-5 w-5" />
                <span className="hidden text-[9px] font-semibold leading-tight lg:block">{t("cart_title")}</span>
                {count > 0 && (
                  <span
                    data-testid="nav-cart-badge"
                    className="absolute end-0 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brandink px-1 text-[9px] font-bold text-white"
                  >
                    {count}
                  </span>
                )}
              </button>
              <button
                data-testid="nav-menu-button"
                onClick={() => setMenuOpen(true)}
                className="flex h-10 w-10 items-center justify-center rounded-md text-brandink transition-colors hover:bg-branddark md:hidden"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div className="px-4 pb-3 md:hidden">{searchBox("nav-search-input-mobile")}</div>
        </div>

        {/* White category bar + dropdowns + mega menu */}
        <div
          className="relative hidden border-b border-line bg-white md:block"
          onMouseLeave={() => setHoveredCat(null)}
        >
          <div className="mx-auto flex max-w-7xl items-center px-4 lg:px-8">
            <button
              data-testid="nav-all-categories"
              onMouseEnter={() => setHoveredCat(null)}
              onClick={() => { setHoveredCat(null); setMegaOpen((o) => !o); }}
              className={`flex shrink-0 items-center gap-2 border-b-2 px-3 py-2.5 text-xs font-bold transition-colors ${
                megaOpen ? "border-brandink text-brandink" : "border-brand text-brandink"
              }`}
            >
              <LayoutGrid className="h-4 w-4" />
              {t("nav_all_categories")}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${megaOpen ? "rotate-180" : ""}`} />
            </button>
            <nav data-testid="nav-links" className="no-scrollbar flex items-center gap-1 overflow-x-auto">
              {categories.map((c) => (
                <button
                  key={c.id}
                  data-testid={`nav-cat-${c.id}`}
                  onMouseEnter={() => { setMegaOpen(false); setHoveredCat(c.id); }}
                  onFocus={() => setHoveredCat(c.id)}
                  onClick={() => pick(c.id)}
                  className={`whitespace-nowrap border-b-2 px-3 py-2.5 text-xs font-medium transition-colors ${
                    hoveredCat === c.id
                      ? "border-brandink text-brandink"
                      : "border-transparent text-brandink/80 hover:border-brand hover:text-brandink"
                  }`}
                >
                  {isAr ? c.name_ar : c.name}
                </button>
              ))}
              <Link
                to="/#contact"
                onClick={(e) => { e.preventDefault(); if (window.location.pathname !== "/") { navigate("/"); setTimeout(() => scrollTo("#contact"), 300); } else scrollTo("#contact"); }}
                onMouseEnter={() => setHoveredCat(null)}
                data-testid="nav-link-contact"
                className="whitespace-nowrap border-b-2 border-transparent px-3 py-2.5 text-xs font-medium text-brandink/80 transition-colors hover:border-brand hover:text-brandink"
              >
                {t("nav_contact")}
              </Link>
            </nav>
          </div>

          {/* Hover dropdown for a single category (noon style) */}
          <AnimatePresence>
            {hovered && (hovered.subs || []).length > 0 && !megaOpen && (
              <motion.div
                key={hovered.id}
                data-testid="nav-dropdown"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.15 }}
                className="absolute left-0 right-0 top-full z-50 border-b border-line bg-white shadow-lg"
              >
                <div className="mx-auto flex max-w-7xl items-start gap-10 px-4 py-5 lg:px-8">
                  <div className="flex-1">
                    <button
                      data-testid={`nav-dropdown-shopall-${hovered.id}`}
                      onClick={() => pick(hovered.id)}
                      className="flex items-center gap-1.5 text-sm font-bold text-brandink transition-colors hover:text-link"
                    >
                      {isAr ? hovered.name_ar : hovered.name}
                      <ArrowRight className="h-3.5 w-3.5 rtl:-scale-x-100" />
                    </button>
                    <div className="mt-3 grid grid-cols-2 gap-x-10 gap-y-2.5 lg:grid-cols-3">
                      {hovered.subs.map((s) => (
                        <button
                          key={s.id}
                          data-testid={`nav-dropdown-sub-${hovered.id}-${s.id}`}
                          onClick={() => pick(hovered.id, s.id)}
                          className="text-start text-xs text-muted transition-colors hover:text-link"
                        >
                          {isAr ? s.name_ar : s.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  <button
                    data-testid={`nav-dropdown-image-${hovered.id}`}
                    onClick={() => pick(hovered.id)}
                    className="group relative hidden h-36 w-56 shrink-0 overflow-hidden rounded-lg lg:block"
                    aria-label={isAr ? hovered.name_ar : hovered.name}
                  >
                    <img
                      src={hovered.hero || hovered.image}
                      alt={isAr ? hovered.name_ar : hovered.name}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <span className="absolute inset-x-0 bottom-0 bg-black/60 px-3 py-2 text-start text-[11px] font-bold uppercase tracking-wide text-white">
                      {isAr ? hovered.name_ar : hovered.name}
                    </span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Full mega menu (all categories) */}
          <AnimatePresence>
            {megaOpen && (
              <>
                <div
                  data-testid="mega-overlay"
                  className="fixed inset-0 z-40 bg-black/20"
                  onClick={() => setMegaOpen(false)}
                />
                <motion.div
                  data-testid="mega-menu"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.18 }}
                  className="absolute left-0 right-0 top-full z-50 border-b border-line bg-white shadow-xl"
                >
                  <div className="mx-auto grid max-h-[62vh] max-w-7xl grid-cols-4 gap-x-6 gap-y-5 overflow-y-auto px-4 py-6 lg:grid-cols-5 lg:px-8">
                    {categories.map((c) => (
                      <div key={c.id}>
                        <button
                          data-testid={`mega-cat-${c.id}`}
                          onClick={() => pick(c.id)}
                          className="text-start text-xs font-bold text-brandink transition-colors hover:text-link"
                        >
                          {isAr ? c.name_ar : c.name}
                        </button>
                        <ul className="mt-2 space-y-1.5">
                          {(c.subs || []).map((s) => (
                            <li key={s.id}>
                              <button
                                data-testid={`mega-sub-${c.id}-${s.id}`}
                                onClick={() => pick(c.id, s.id)}
                                className="text-start text-[11px] text-muted transition-colors hover:text-link"
                              >
                                {isAr ? s.name_ar : s.name}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex flex-col bg-white"
          >
            <div className="flex items-center justify-between bg-brand px-4 py-3">
              <span className="text-xl font-extrabold lowercase tracking-tight text-brandink">cambroo</span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setMenuOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-md text-brandink hover:bg-branddark"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex flex-1 flex-col gap-1 overflow-y-auto px-4 py-6">
              {/* Location picker (mobile) */}
              <div className="mb-3 rounded-lg border border-line p-3">
                <p className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-muted">
                  <MapPin className="h-3.5 w-3.5" /> {t("deliver_to")}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {EMIRATES.map((e) => (
                    <button
                      key={e}
                      data-testid={`mobile-location-${e.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => setEmirate(e)}
                      className={`rounded-full border px-3 py-1.5 text-[11px] font-semibold ${
                        emirate === e ? "border-brandink bg-brandink text-white" : "border-line text-brandink/70"
                      }`}
                    >
                      {EMIRATE_NAMES[lang][e] || e}
                    </button>
                  ))}
                </div>
              </div>
              <button
                data-testid="mobile-nav-link-all"
                onClick={() => { setMenuOpen(false); goToFilter(navigate, "all"); }}
                className="rounded-md px-3 py-3 text-start text-base font-semibold text-brandink hover:bg-page"
              >
                {t("nav_all_categories")}
              </button>
              {categories.map((c) => (
                <div key={c.id}>
                  <button
                    data-testid={`mobile-nav-link-${c.id}`}
                    onClick={() => setExpanded((x) => (x === c.id ? null : c.id))}
                    className="flex w-full items-center justify-between rounded-md px-3 py-3 text-start text-base font-medium text-brandink/90 hover:bg-page"
                  >
                    {isAr ? c.name_ar : c.name}
                    <ChevronDown className={`h-4 w-4 text-muted transition-transform ${expanded === c.id ? "rotate-180" : ""}`} />
                  </button>
                  {expanded === c.id && (
                    <div className="ms-4 border-s-2 border-line ps-3">
                      <button
                        data-testid={`mobile-sub-${c.id}-all`}
                        onClick={() => pick(c.id)}
                        className="block w-full rounded px-3 py-2 text-start text-sm font-semibold text-brandink hover:bg-page"
                      >
                        {t("tab_all")}
                      </button>
                      {(c.subs || []).map((s) => (
                        <button
                          key={s.id}
                          data-testid={`mobile-sub-${c.id}-${s.id}`}
                          onClick={() => pick(c.id, s.id)}
                          className="block w-full rounded px-3 py-2 text-start text-sm text-muted hover:bg-page hover:text-brandink"
                        >
                          {isAr ? s.name_ar : s.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <Link
                to="/wishlist"
                onClick={() => setMenuOpen(false)}
                data-testid="mobile-nav-link-wishlist"
                className="rounded-md px-3 py-3 text-base font-medium text-brandink/90 hover:bg-page"
              >
                {t("nav_wishlist")}{wishlist.length > 0 ? ` (${wishlist.length})` : ""}
              </Link>
              <Link
                to="/account"
                onClick={() => setMenuOpen(false)}
                data-testid="mobile-nav-link-account"
                className="mt-2 rounded-md bg-brand px-3 py-3 text-center text-sm font-bold text-brandink"
              >
                {t("nav_account")}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
